import { Col, Form, Modal, ModalBody, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfo,
  faMagnifyingGlass,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { DropdownSelect } from "components/DropdownSelect";
import { CustomInputModal } from "components/CustomInputModal";
import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import { useEmpresas } from "hooks/useEmpresas";
import { useDivisoes } from "hooks/useDivisoes";
import { useCentroCusto } from "hooks/useCentroCusto";
import { useServicoModelo } from "hooks/useServicoModelo";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  IFiltroProcessoVincularLoteGP,
  IProcessoVincularLoteGP,
} from "../../../hooks/useCriarLote";
import {
  BotoesFiltro,
  Button,
  FiltrosContainer,
  IconeTooltipServico,
  InputPesquisa,
  TabelaHeader,
  WrapperPesquisa,
} from "./style";

interface IProps {
  show: boolean;
  handleClose: () => void;
  processosVincular: IProcessoVincularLoteGP[];
  selectedRows: IProcessoVincularLoteGP[];
  setSelectedRows: (rows: IProcessoVincularLoteGP[]) => void;
  clearRows: boolean;
  onBuscar: (filtros: IFiltroProcessoVincularLoteGP) => void;
  onAdicionarCusto: () => void;
  validarProcessos: (processos: (number | string)[]) => any;
  validarPlacasChassis: (placasChassis: string[]) => any;
  onClickProcesso: (idProcesso: number) => void;
}

export function ModalSelecionarProcessos({
  show,
  handleClose,
  processosVincular,
  selectedRows,
  setSelectedRows,
  clearRows,
  onBuscar,
  onAdicionarCusto,
  validarProcessos,
  validarPlacasChassis,
  onClickProcesso,
}: IProps) {
  const { control, handleSubmit, setValue } =
    useForm<IFiltroProcessoVincularLoteGP>();
  const [empresaSelecionada, setEmpresaSelecionada] = useState<
    number | undefined
  >();
  const [textoBusca, setTextoBusca] = useState("");
  const [dadosFiltrados, setDadosFiltrados] = useState<
    IProcessoVincularLoteGP[]
  >([]);

  const { empresasOptions } = useEmpresas();
  const { divisoesOptions, listarTodasDivisoes } = useDivisoes("");
  const { centrosCustoOptions } = useCentroCusto(empresaSelecionada, null);
  const { servicosModeloOptions, listarServicosModelo } = useServicoModelo();

  useEffect(() => {
    if (show) listarServicosModelo({ atualizarOptions: true });
  }, [show]);

  useEffect(() => {
    listarTodasDivisoes(empresaSelecionada);
    if (!empresaSelecionada) {
      setValue("servico", undefined);
      setValue("divisao", undefined);
      setValue("centroCusto", undefined);
    }
  }, [empresaSelecionada]);

  useEffect(() => {
    const busca = textoBusca.toLowerCase().replace("-", "");
    if (!busca) {
      setDadosFiltrados(processosVincular);
      return;
    }
    setDadosFiltrados(
      processosVincular.filter(
        (p) =>
          String(p.idProcesso).includes(busca) ||
          (p.placa && p.placa.replace("-", "").toLowerCase().includes(busca)),
      ),
    );
  }, [textoBusca, processosVincular]);

  const onLimpar = () => {
    setValue("processos", []);
    setValue("placasChassis", []);
    setValue("empresa", undefined);
    setValue("divisao", undefined);
    setValue("servico", undefined);
    setValue("centroCusto", undefined);
    setEmpresaSelecionada(undefined);
    setTextoBusca("");
  };

  useEffect(() => {
    if (show) onLimpar();
  }, [show]);

  const columns: IColum<IProcessoVincularLoteGP>[] = [
    {
      name: "Processo",
      selector: ({ idProcesso }) => (
        <span
          className="link-primary"
          style={{ cursor: "pointer" }}
          onClick={() => onClickProcesso(idProcesso)}
        >
          {idProcesso}
        </span>
      ),
      width: "100px",
    },
    {
      name: "Placa",
      selector: ({ placa }) => placa ?? "--",
      width: "110px",
    },
    {
      name: "Renavam",
      selector: ({ renavam }) => renavam ?? "--",
      width: "130px",
    },
    {
      name: "Chassi",
      selector: ({ chassi }) => chassi ?? "--",
      width: "180px",
    },
    {
      name: "Empresa",
      selector: ({ nomeEmpresa }) => nomeEmpresa ?? "--",
      width: "180px",
    },
    {
      name: "Divisão",
      selector: ({ nomeDivisao }) => nomeDivisao ?? "--",
      width: "160px",
    },
    {
      name: "Centro de custo",
      selector: ({ nomeCentroCusto }) => nomeCentroCusto ?? "--",
      width: "180px",
    },
    {
      name: "Serviço",
      selector: ({ servico }) => servico ?? "--",
      width: "160px",
    },
  ];

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard
      size="xl"
      centered
    >
      <Modal.Header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 20px",
        }}
      >
        <Modal.Title style={{ fontSize: "1.1rem", fontWeight: "bold" }}>
          Adicionar custo
        </Modal.Title>
        <span style={{ cursor: "pointer" }} onClick={handleClose}>
          <FontAwesomeIcon icon={faXmark} />
        </span>
      </Modal.Header>
      <ModalBody style={{ padding: "16px 20px" }}>
        <p style={{ fontSize: "0.9rem", color: "#666", marginBottom: "1rem" }}>
          Selecione o(s) processo(s) que deseja adicionar o custo.
        </p>

        <FiltrosContainer>
          <Form noValidate onSubmit={handleSubmit(onBuscar)}>
            <Row>
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label>ID Processo</Form.Label>
                  <Controller
                    control={control}
                    name="processos"
                    defaultValue={[]}
                    render={({ field: { onChange, value } }) => (
                      <CustomInputModal
                        label="Filtrar em massa"
                        value={value ?? []}
                        placeholder="Busque por ID"
                        onConfirm={onChange}
                        onClickOk={validarProcessos}
                        validar
                        style={{ width: "100%" }}
                        placeholderModal="Digite ou cole os IDs dos processos e tecle ENTER"
                        labelItem="ID Processo"
                        zIndex={1070}
                        naoFecharForaModal
                      />
                    )}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label>Placa ou chassi</Form.Label>
                  <Controller
                    control={control}
                    name="placasChassis"
                    defaultValue={[]}
                    render={({ field: { onChange, value } }) => (
                      <CustomInputModal
                        label="Filtrar em massa"
                        value={value ?? []}
                        placeholder="Busque pelo veículo"
                        onConfirm={onChange}
                        onClickOk={validarPlacasChassis}
                        validar
                        style={{ width: "100%" }}
                        placeholderModal="Digite ou cole as placas/chassis e tecle ENTER"
                        labelItem="Placa ou chassi"
                        zIndex={1070}
                        naoFecharForaModal
                      />
                    )}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label>Empresa</Form.Label>
                  <Controller
                    control={control}
                    name="empresa"
                    render={({ field: { onChange } }) => (
                      <DropdownSelect
                        options={empresasOptions}
                        selectedValue={empresaSelecionada}
                        onSelect={(v) => {
                          onChange(v);
                          setEmpresaSelecionada(v as number);
                        }}
                        searchable
                        title=""
                      />
                    )}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label>Divisão</Form.Label>
                  <Controller
                    control={control}
                    name="divisao"
                    render={({ field: { onChange, value } }) => (
                      <DropdownSelect
                        options={divisoesOptions}
                        selectedValue={value}
                        onSelect={onChange}
                        searchable
                        disabled={!empresaSelecionada}
                        title=""
                      />
                    )}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row style={{ marginTop: "0.5rem" }}>
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label>
                    Serviço{" "}
                    {!empresaSelecionada && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Selecione uma empresa para habilitar este filtro
                          </Tooltip>
                        }
                      >
                        <IconeTooltipServico>
                          <FontAwesomeIcon icon={faInfo} />
                        </IconeTooltipServico>
                      </OverlayTrigger>
                    )}
                  </Form.Label>
                  <Controller
                    control={control}
                    name="servico"
                    render={({ field: { onChange, value } }) => (
                      <DropdownSelect
                        options={servicosModeloOptions}
                        selectedValue={value}
                        onSelect={onChange}
                        searchable
                        disabled={!empresaSelecionada}
                        title=""
                      />
                    )}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label>Centro de custo</Form.Label>
                  <Controller
                    control={control}
                    name="centroCusto"
                    render={({ field: { onChange, value } }) => (
                      <DropdownSelect
                        options={centrosCustoOptions}
                        selectedValue={value}
                        onSelect={onChange}
                        disabled={!empresaSelecionada}
                        title=""
                      />
                    )}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <BotoesFiltro>
                  <Button
                    type="reset"
                    outline
                    variante="primary"
                    onClick={onLimpar}
                  >
                    Limpar
                  </Button>
                  <Button type="submit">Buscar</Button>
                </BotoesFiltro>
              </Col>
            </Row>
          </Form>
        </FiltrosContainer>

        <TabelaHeader>
          <WrapperPesquisa>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <InputPesquisa
              placeholder="Buscar"
              value={textoBusca}
              onChange={(e) => setTextoBusca(e.target.value)}
            />
          </WrapperPesquisa>
        </TabelaHeader>

        <Table
          keyField="idProcesso"
          columns={columns}
          data={dadosFiltrados}
          loading={false}
          noSubHeader
          possuiTooltip={false}
          selectableRows
          setRowsSelected={setSelectedRows}
          headerFixo
          clearSelectedRows={clearRows}
        />
        <hr></hr>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            marginTop: "1rem",
          }}
        >
          <Button outline variante="primary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            variante="primary"
            onClick={onAdicionarCusto}
            disabled={!selectedRows.length}
          >
            Adicionar custo
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}
